import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
export function Sidebar() {
  const {
    currentEra
  } = useTheme();
  // Era-specific CTA box styles
  const getCTABoxStyles = () => {
    switch (currentEra) {
      case '60s':
        return 'bg-black text-white p-4 relative overflow-hidden';
      case '70s':
        return 'bg-amber-600 text-white p-4 relative overflow-hidden rounded-lg';
      case '80s':
        return 'bg-yellow-400 text-black p-4 relative overflow-hidden';
      case '90s':
        return 'bg-yellow-400 text-black p-4 relative overflow-hidden';
      case '2000s':
        return 'bg-orange-500 text-white p-4 relative overflow-hidden rounded shadow';
      default:
        return 'bg-yellow-400 text-black p-4 relative overflow-hidden';
    }
  };
  // Era-specific CTA button styles
  const getCTAButtonStyles = () => {
    switch (currentEra) {
      case '60s':
        return 'bg-white text-black px-4 py-2 text-sm font-bold uppercase relative z-10';
      case '70s':
        return 'bg-white text-amber-800 px-4 py-2 text-sm font-bold uppercase relative z-10 rounded-full';
      case '80s':
        return 'bg-black text-white px-4 py-2 text-sm font-bold uppercase relative z-10';
      case '90s':
        return 'bg-black text-white px-4 py-2 text-sm font-bold uppercase relative z-10';
      case '2000s':
        return 'bg-blue-600 text-white px-4 py-2 text-sm font-bold uppercase relative z-10 rounded shadow hover:bg-blue-700 transition-colors';
      default:
        return 'bg-black text-white px-4 py-2 text-sm font-bold uppercase relative z-10';
    }
  };
  // Era-specific voices box styles
  const getVoicesBoxStyles = () => {
    switch (currentEra) {
      case '60s':
        return 'border-2 border-black p-4';
      case '70s':
        return 'border-2 border-amber-800 p-4 rounded-lg';
      case '80s':
        return 'border-2 border-cyan-400 p-4';
      case '90s':
        return 'border-2 border-cyan-400 p-4';
      case '2000s':
        return 'border-2 border-blue-600 p-4 rounded shadow-sm';
      default:
        return 'border-2 border-cyan-400 p-4';
    }
  };
  // Era-specific voices title styles
  const getVoicesTitleStyles = () => {
    switch (currentEra) {
      case '60s':
        return 'text-xl font-bold uppercase mb-4 text-black';
      case '70s':
        return 'text-xl font-bold uppercase mb-4 text-amber-800';
      case '80s':
        return 'text-xl font-bold uppercase mb-4 text-cyan-400';
      case '90s':
        return 'text-xl font-bold uppercase mb-4 text-cyan-400';
      case '2000s':
        return 'text-xl font-bold uppercase mb-4 text-blue-600';
      default:
        return 'text-xl font-bold uppercase mb-4 text-cyan-400';
    }
  };
  // Era-specific category label styles
  const getCategoryLabelStyles = (type: string) => {
    switch (currentEra) {
      case '60s':
        return 'text-sm font-bold uppercase mb-1 text-black';
      case '70s':
        return 'text-sm font-bold uppercase mb-1 text-amber-800';
      case '80s':
        return type === 'interview' ? 'text-sm font-bold uppercase mb-1 text-pink-500' : type === 'opinion' ? 'text-sm font-bold uppercase mb-1 text-yellow-400' : 'text-sm font-bold uppercase mb-1 text-purple-600';
      case '90s':
        return type === 'interview' ? 'text-sm font-bold uppercase mb-1 text-pink-500' : type === 'opinion' ? 'text-sm font-bold uppercase mb-1 text-yellow-400' : 'text-sm font-bold uppercase mb-1 text-purple-600';
      case '2000s':
        return type === 'interview' ? 'text-sm font-bold uppercase mb-1 text-orange-500' : type === 'opinion' ? 'text-sm font-bold uppercase mb-1 text-green-500' : 'text-sm font-bold uppercase mb-1 text-purple-600';
      default:
        return type === 'interview' ? 'text-sm font-bold uppercase mb-1 text-pink-500' : type === 'opinion' ? 'text-sm font-bold uppercase mb-1 text-yellow-400' : 'text-sm font-bold uppercase mb-1 text-purple-600';
    }
  };
  // Era-specific article title styles
  const getArticleTitleStyles = () => {
    switch (currentEra) {
      case '60s':
        return 'text-lg font-bold mb-2 text-black';
      case '70s':
        return 'text-lg font-bold mb-2 text-amber-900';
      case '80s':
        return 'text-lg font-bold mb-2 text-white';
      case '90s':
        return 'text-lg font-bold mb-2 text-white';
      case '2000s':
        return 'text-lg font-bold mb-2 text-blue-600';
      default:
        return 'text-lg font-bold mb-2 text-white';
    }
  };
  // Era-specific article text styles
  const getArticleTextStyles = () => {
    switch (currentEra) {
      case '60s':
        return 'text-sm text-black';
      case '70s':
        return 'text-sm text-amber-900';
      case '80s':
        return 'text-sm text-gray-300';
      case '90s':
        return 'text-sm text-white';
      case '2000s':
        return 'text-sm text-gray-700';
      default:
        return 'text-sm text-white';
    }
  };
  // Era-specific pull quote styles
  const getPullQuoteStyles = () => {
    switch (currentEra) {
      case '60s':
        return 'bg-black text-white p-6 relative';
      case '70s':
        return 'bg-gradient-to-r from-amber-800 to-amber-600 p-6 relative rounded-lg';
      case '80s':
        return 'bg-gradient-to-r from-pink-500 to-purple-600 p-6 relative';
      case '90s':
        return 'bg-gradient-to-r from-pink-500 to-purple-600 p-6 relative';
      case '2000s':
        return 'bg-gradient-to-r from-blue-600 to-orange-500 p-6 relative rounded shadow';
      default:
        return 'bg-gradient-to-r from-pink-500 to-purple-600 p-6 relative';
    }
  };
  // Era-specific CTA geometric element
  const getCTAGeometricElement = () => {
    switch (currentEra) {
      case '60s':
        return <div className="absolute -right-4 -top-4 w-16 h-16 bg-white rotate-12" data-id="element-199"></div>;
      case '70s':
        return <div className="absolute -right-4 -top-4 w-16 h-16 bg-amber-800 rounded-full" data-id="element-200"></div>;
      case '80s':
        return <div className="absolute -right-4 -top-4 w-16 h-16 bg-pink-500 rotate-12" data-id="element-201"></div>;
      case '90s':
        return <div className="absolute -right-4 -top-4 w-16 h-16 bg-pink-500 rotate-12" data-id="element-202"></div>;
      case '2000s':
        return <div className="absolute -right-4 -top-4 w-16 h-16 bg-blue-600 rounded-lg shadow" data-id="element-203"></div>;
      default:
        return <div className="absolute -right-4 -top-4 w-16 h-16 bg-pink-500 rotate-12" data-id="element-204"></div>;
    }
  };
  return <aside className="col-span-1 md:col-span-4 space-y-6" data-id="element-205">
      {/* Call to action box */}
      <div className={getCTABoxStyles()} data-id="element-206">
        {getCTAGeometricElement()}
        <h3 className="text-2xl font-bold uppercase mb-2 relative z-10" data-id="element-207">
          Join Us!
        </h3>
        <p className="text-sm mb-4 relative z-10" data-id="element-208">
          Be part of the movement. Submit your art, writing, and ideas.
        </p>
        <button className={getCTAButtonStyles()} data-id="element-209">Submit Now</button>
      </div>
      {/* Mini articles */}
      <div className={getVoicesBoxStyles()} data-id="element-210">
        <h3 className={getVoicesTitleStyles()} data-id="element-211">Voices From The Front</h3>
        <div className="space-y-4" data-id="element-212">
          <div className={`pb-4 border-b border-dashed ${currentEra === '60s' ? 'border-gray-800' : currentEra === '70s' ? 'border-amber-700' : currentEra === '80s' ? 'border-gray-700' : currentEra === '90s' ? 'border-gray-700' : 'border-gray-300'}`} data-id="element-213">
            <p className={getCategoryLabelStyles('interview')} data-id="element-214">Interview</p>
            <h4 className={getArticleTitleStyles()} data-id="element-215">"We Won't Be Erased"</h4>
            <p className={getArticleTextStyles()} data-id="element-216">
              Activist Lee Chen discusses the importance of visibility in an
              increasingly hostile political climate.
            </p>
          </div>
          <div className={`pb-4 border-b border-dashed ${currentEra === '60s' ? 'border-gray-800' : currentEra === '70s' ? 'border-amber-700' : currentEra === '80s' ? 'border-gray-700' : currentEra === '90s' ? 'border-gray-700' : 'border-gray-300'}`} data-id="element-217">
            <p className={getCategoryLabelStyles('opinion')} data-id="element-218">Opinion</p>
            <h4 className={getArticleTitleStyles()} data-id="element-219">
              Reclaiming Our Narrative
            </h4>
            <p className={getArticleTextStyles()} data-id="element-220">
              Why DIY media matters in the age of corporate rainbow-washing.
            </p>
          </div>
          <div data-id="element-221">
            <p className={getCategoryLabelStyles('art')} data-id="element-222">Art Feature</p>
            <h4 className={getArticleTitleStyles()} data-id="element-223">
              Protest Posters That Changed History
            </h4>
            <p className={getArticleTextStyles()} data-id="element-224">
              A visual journey through iconic queer protest art from the 70s to
              now.
            </p>
          </div>
        </div>
      </div>
      {/* Pull quote */}
      <div className={getPullQuoteStyles()} data-id="element-225">
        <div className="absolute left-2 top-2 text-6xl font-bold opacity-20" data-id="element-226">
          "
        </div>
        <p className="text-xl font-bold italic mb-2 pl-4" data-id="element-227">
          We're not just making magazines. We're documenting our resistance.
        </p>
        <p className="text-right text-sm" data-id="element-228">- Alex Marquez, Founder</p>
      </div>
    </aside>;
}